import {Avatar} from 'platform/components';
import {
  Box,
  Center,
  Clickable,
  Heading,
  Hide,
  HStack,
  Show,
  Space,
  Text,
  VStack,
} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useReadCodeQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {useGetWorkshopInterestQuery} from '@dms/api/workshop';
import {
  ActivityDate,
  ActivitySidebarLink,
  getActivityIconColor,
  transformWorkshopActivityItem,
  WorkshopActivityListItem,
} from '@dms/features/activity-panel';
import {openEditTaskFormDialog} from '@dms/features/tasks';
import {getNaturalPersonFullName} from '@dms/shared';

import {DOT_CHARACTER} from 'shared/web';

type ActivityItemProps = {
  item: WorkshopActivityListItem;
  showLineToNextItem: boolean;
  interestId: string;
};

export function ActivityItem(props: ActivityItemProps) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const {data: sourceCodeData} = useReadCodeQuery(
    {codelistId: 'workshop_interest_source', codeId: workshopInterest?.sourceCodeId ?? ''},
    {skip: !workshopInterest?.sourceCodeId}
  );

  const {data: closeReasonData} = useReadCodeQuery(
    {
      codelistId: 'workshop_interest_close_reason',
      codeId: props.item.reasonCloseCodeId ?? '',
    },
    {skip: isNil(props.item.reasonCloseCodeId)}
  );

  const {data: userData} = useGetUserQuery(
    {id: props.item.userId ?? ''},
    {skip: !props.item.userId}
  );

  const handleOpenEditTaskFormDialog = (id: string) => {
    openEditTaskFormDialog({
      id,
      'data-testid': 'workshop-interest-activity-panel-task-edit',
    });
  };

  const transformedItem = transformWorkshopActivityItem(
    props.item,
    {source: sourceCodeData, closeReason: closeReasonData},
    handleOpenEditTaskFormDialog
  );

  const hasUserName = isNotNil(userData);

  if (!transformedItem) {
    return null;
  }

  return (
    <HStack spacing={2}>
      <VStack>
        <Avatar
          color={transformedItem.color}
          icon={transformedItem.icon}
          variant="square"
          iconColor={getActivityIconColor(transformedItem.color)}
        />
        <Hide when={props.showLineToNextItem}>
          <Center width="100%" height="100%">
            <Box height="100%" backgroundColor="general.separator" width="0.125rem" />
          </Center>
        </Hide>
      </VStack>
      <VStack width="100%">
        <Box paddingBottom={4}>
          <VStack spacing={1}>
            <Box paddingBottom={0}>
              <HStack width="100%">
                <VStack width="100%" spacing={1}>
                  <Clickable
                    onClick={transformedItem.onClick}
                    isDisabled={!transformedItem.isEditable}
                  >
                    <Heading size={5}>{transformedItem.title}</Heading>
                  </Clickable>
                  <Show when={transformedItem.interestType || hasUserName}>
                    <Text color="tertiary" size="xSmall">
                      <HStack align="center">
                        <Show when={transformedItem.interestType}>
                          {transformedItem.interestType}
                        </Show>
                        <Show when={transformedItem.interestType && hasUserName}>
                          <Space horizontal={1} />
                          {DOT_CHARACTER}
                          <Space horizontal={1} />
                        </Show>
                        <Show when={hasUserName}>
                          {transformedItem.userName ?? getNaturalPersonFullName(userData)}
                        </Show>
                      </HStack>
                    </Text>
                  </Show>
                  <Show when={isNotNilOrEmpty(transformedItem.description)}>
                    <Box maxWidth={62}>
                      <Text size="xSmall" color="secondary" noWrap>
                        {transformedItem.description}
                      </Text>
                    </Box>
                  </Show>
                  <Show when={isNotNilOrEmpty(transformedItem.links)}>
                    <HStack>
                      {transformedItem.links?.map((link) => (
                        <ActivitySidebarLink key={link.label} {...link} />
                      ))}
                    </HStack>
                  </Show>
                </VStack>
                <ActivityDate timestamp={props.item.timestamp} />
              </HStack>
            </Box>
          </VStack>
        </Box>
      </VStack>
    </HStack>
  );
}
